// The extraction instructions and the model are owned by the Smart Import
// service (see .devin/workers/smart-import-proxy), so the browser cannot
// override them. Only the per-chunk user content is built here.

// The service rejects requests carrying more than 12 images. Chunks are
// budgeted to the same limit so every referenced image is actually sent;
// anything beyond it would be dropped from the payload only.
export const MAX_IMAGES_PER_REQUEST = 12;

const SECTION_HEADER = (label, position, total) =>
  `--- Section ${position} of ${total} (source: ${label}) ---`;

/**
 * Build the multimodal user content for one chunk.
 * @param {Array<{label: string, text: string, imageIds: string[]}>} sections
 * @param {Object<string, string>} imageDataUris imageId -> data URI
 * @param {{startNumber?: number}} [options] when set, questions without an
 *        explicit number in the source are numbered sequentially continuing
 *        from it — auto-numbered Word lists carry no number in their text,
 *        so without this every chunk restarts at 1 and the post-run dedup
 *        drops distinct questions as "duplicate numbers".
 */
export function buildChunkUserContent(
  sections,
  imageDataUris,
  { startNumber } = {}
) {
  const usedImageIds = [];
  const continuationHint =
    startNumber > 1
      ? ` When a question has no explicit number in the source, number it sequentially continuing from ${startNumber}.`
      : "";
  const textParts = [
    `Extract the questions from the ${sections.length} section(s) below. Sections are separated by headers and contain whole questions.${continuationHint}`
  ];

  sections.forEach((section, index) => {
    textParts.push(
      SECTION_HEADER(section.label, index + 1, sections.length)
    );
    for (const imageId of section.imageIds) {
      textParts.push(`[${imageId}]`);
      usedImageIds.push(imageId);
    }
    textParts.push(section.text || "(no text)");
  });

  const content = [{ type: "text", text: textParts.join("\n") }];

  for (const imageId of usedImageIds.slice(
    0,
    MAX_IMAGES_PER_REQUEST
  )) {
    const uri = imageDataUris[imageId];
    if (uri) {
      content.push({
        type: "image_url",
        image_url: { url: uri }
      });
    }
  }

  return { content, usedImageIds };
}

/** Text-only variant used when the model/provider rejects images. */
export function buildTextOnlyUserContent(sections, options) {
  return buildChunkUserContent(sections, {}, options);
}
